// src/components/layout/DashboardLayout.jsx
import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import OnboardingWidget from '../onboarding/OnboardingWidget';
import DemoBanner from '../DemoBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { cn } from '../../lib/utils';

// AUTO_COLLAPSE_PATHS removed — sidebar should never auto-collapse on navigation

const pageTitles = {
  '/dashboard': 'Overview',
  '/funnels': 'Funnels',
  '/landing-pages': 'Landing Pages',
  '/ab-tests': 'A/B Tests',
  '/results': 'Results',
  '/analytics': 'Analytics',
  '/meta-ads': 'Meta Ads',
  '/google-ads': 'Google Ads',
  '/tiktok-ads': 'TikTok Ads',
  '/adsense': 'AdSense Revenue',
  '/ad-manager': 'Ad Manager Revenue',
  '/shopify': 'Shopify Stores',
  '/tiktok-shop': 'TikTok Shop',
  '/ecommerce': 'Ecommerce Analytics',
  '/publisher': 'Publisher Analytics',
  '/hapiguard': 'HapiGuard',
  '/email-marketing': 'Email Marketing',
  '/team': 'Team',
  '/custom-domains': 'Custom Domains',
  '/affiliate': 'Affiliate Program',
  '/subscribe': 'Subscription Plans',
  '/support': 'Support',
  '/settings': 'Settings',
  '/admin': 'Admin Panel',
  '/system-admin': 'System Admin',
};

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile only
  const location = useLocation();
  const { isDemo } = useAuth();
  const { collapsed } = useSidebar();

  // Close mobile drawer on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const getTitle = () => {
    if (pageTitles[location.pathname]) return pageTitles[location.pathname];
    if (location.pathname.match(/^\/(ab-)?tests\//) && location.pathname.includes('/analytics')) return 'Test Analytics';
    if (location.pathname.match(/^\/(ab-)?tests\//)) return 'Test Details';
    if (location.pathname.startsWith('/landing-pages/legacy')) return 'Landing Page Builder (legacy)';
    for (const [path, title] of Object.entries(pageTitles)) {
      if (location.pathname.startsWith(path)) return title;
    }
    return 'Overview';
  };

  return (
    // h-screen + overflow-hidden on root forces the page to fit the viewport
    // so only <main> scrolls — this is what makes the header sticky on mobile
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Demo banner */}
      {isDemo && <DemoBanner />}

      <div className="flex flex-1 min-h-0">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar — fixed on mobile, static on desktop */}
        <aside className={cn(
          'fixed inset-y-0 left-0 z-50 transform transition-all duration-200 ease-in-out lg:relative lg:translate-x-0 lg:flex-shrink-0',
          collapsed ? 'w-16' : 'w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </aside>

        {/* Main content column — flex-col, header sticks, only main scrolls */}
        <div className={cn(
          'flex-1 flex flex-col min-w-0 min-h-0 transition-all duration-200 overflow-hidden',
          collapsed ? 'lg:pl-0' : 'lg:pl-0'
        )}>
          {/* Header — sticky top-0 is set inside Header.jsx itself */}
          <Header onMenuClick={() => setSidebarOpen(true)} title={getTitle()} />
          {/* Scrollable content */}
          <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Onboarding widget — rendered outside scroll context */}
      {!isDemo && <OnboardingWidget />}
    </div>
  );
};

export default DashboardLayout;
